import CreateTaskModal from '@/components/features/Projects/modals/CreateTaskModal';
import EditTaskModal from '@/components/features/Projects/modals/EditTaskModal';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/shadcn/tooltip';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { laravelJsonFetch, LaravelJsonRequestError } from '@/lib/laravelJsonFetch';
import { getGanttInstanceFactory, getGanttSingleton, type DhtmlxGanttInstance } from '@/lib/projects/dhtmlxGanttFactory';
import {
    buildGanttTimelineCellClasses,
    formatLocalDateYmd,
    parseLocalCalendarDate,
    startOfIsoWeekMonday,
    startOfLocalDay,
} from '@/lib/projects/ganttCalendar';
import { buildGanttTaskStatusCssRules, ganttStatusClassSlug, resolveTaskStatusSwatchFromSettings } from '@/lib/projects/ganttTaskStatusStyles';
import { transformTasksToGantt } from '@/lib/projects/ganttTransform';
import { taskUpdateDenialReason, type TaskUpdateDenialReason } from '@/lib/projects/taskDepartmentAccess';
import { cn } from '@/lib/utils';
import { ProjectTask } from '@/types';
import { usePage } from '@inertiajs/react';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import { ArrowUpDown, Calendar, CalendarRange, Check, PanelLeftClose, Search, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface Props {
    tasks: ProjectTask[];
    projectId: string; // UUID
    projectOwnerId?: string | null; // UUID of the project owner (project_owner_id)
    users: { id: string; name: string }[]; // UUID
    departmentSortActive?: boolean;
    onToggleDepartmentSort?: () => void;
}

type GanttPageProps = {
    locale?: string;
    auth?: {
        user?: { id?: string };
        abilities?: string[];
        roles?: string[];
    };
};

type GanttGridTemplateTask = {
    id?: string;
    text?: string;
    assigned_to?: string;
    type?: string;
    status?: string;
    department_key?: string;
    department_label?: string;
    is_project_timeline?: boolean;
    start_date?: string;
    end_date?: string;
    duration?: number;
    timeline_health?: 'ok' | 'warning' | 'late';
};

interface LinkData {
    source: string;
    target: string;
    type?: string;
}

interface PendingTimelineMove {
    taskId: string;
    startDate: string;
    endDate: string;
}

function resolveGanttBranchKey(chart: DhtmlxGanttInstance, parent: string | number | undefined | null): string | number {
    if (parent === undefined || parent === null || parent === '') {
        const rootId = chart.config?.root_id;
        if (rootId !== undefined && rootId !== null) {
            return rootId;
        }

        return 0;
    }

    return parent;
}

function branchKeysEqual(a: string | number, b: string | number): boolean {
    return String(a) === String(b);
}

function ganttParentRawToApiParentTaskId(parentRaw: string | number | undefined | null): string | null {
    if (parentRaw === undefined || parentRaw === null || parentRaw === '') {
        return null;
    }
    const key = String(parentRaw);
    if (key === '0' || key === '') {
        return null;
    }

    return key;
}

function buildReorderPayloadFromBranch(
    chart: DhtmlxGanttInstance,
    parentRaw: string | number | undefined | null,
): { tasks: { id: string; sort_order: number }[] } | null {
    if (typeof chart.getChildren !== 'function') {
        return null;
    }
    const branchKey = resolveGanttBranchKey(chart, parentRaw);
    const children = chart.getChildren(branchKey);
    if (!Array.isArray(children) || children.length === 0) {
        return null;
    }
    return {
        tasks: children.map((taskId: string | number, index: number) => ({
            id: String(taskId),
            sort_order: index,
        })),
    };
}

function chartTaskIsProjectTimeline(chart: DhtmlxGanttInstance, id: string | number): boolean {
    try {
        const task = chart.getTask(id) as { is_project_timeline?: boolean } | null;
        return Boolean(task?.is_project_timeline);
    } catch {
        return false;
    }
}

function branchHasMovedProjectTimeline(chart: DhtmlxGanttInstance, parentRaw: string | number | undefined | null): boolean {
    if (typeof chart.getChildren !== 'function') {
        return false;
    }
    const branchKey = resolveGanttBranchKey(chart, parentRaw);
    const children = chart.getChildren(branchKey);
    if (!Array.isArray(children) || children.length === 0) {
        return false;
    }

    const timelineIndex = children.findIndex((taskId: string | number) => chartTaskIsProjectTimeline(chart, taskId));
    return timelineIndex > 0;
}

type ViewMode = 'day' | 'week' | 'month' | 'quarter';
type TimelineHealth = 'ok' | 'warning' | 'late';
type GanttScrollState = { x: number; y: number };

/** Zoom levels (column width per day in px). Index 0 = max zoom out (default). */
const GANTT_ZOOM_LEVELS = [18, 22, 28, 36, 48, 65, 90] as const;
const GANTT_ZOOM_DEFAULT_INDEX = 0;
const GANTT_MONTH_VIEW_COLUMN_WIDTH = GANTT_ZOOM_LEVELS[GANTT_ZOOM_DEFAULT_INDEX];
const GANTT_DEFAULT_COLUMN_WIDTH = GANTT_ZOOM_LEVELS[GANTT_ZOOM_DEFAULT_INDEX];
const GANTT_ROW_HEIGHT = 46;
const GANTT_BAR_HEIGHT = 28;
/** Matches historical single-column grid width for the task name column. */
const GANTT_GRID_TREE_COL_WIDTH = 360;
/** Dedicated grid column for start date (`YYYY-MM-DD`); not shared with the end column. */
const GANTT_GRID_START_DATE_COL_WIDTH = 108;
/** Dedicated grid column for end date (`YYYY-MM-DD`). */
const GANTT_GRID_END_DATE_COL_WIDTH = 112;
/** Action column shown after end date for quick add-child button. */
const GANTT_GRID_ACTION_COL_WIDTH = 44;
const GANTT_GRID_WIDTH = GANTT_GRID_TREE_COL_WIDTH + GANTT_GRID_START_DATE_COL_WIDTH + GANTT_GRID_END_DATE_COL_WIDTH + GANTT_GRID_ACTION_COL_WIDTH;
const GANTT_INLINE_MIN_HEIGHT = 288;
const GANTT_DAY_VIEW_FOCUS_FUTURE_DAYS = 14;
const GANTT_DATA_PAD_BEFORE_DAYS = 21;
const GANTT_DATA_PAD_AFTER_DAYS = 45;
const GANTT_MIN_EMPTY_SPAN_DAYS = 90;
const GANTT_TODAY_MARKER_ID = 'crm-gantt-today';
/** Collapses burst DHTMLX events into one async write + toast (timeline drag, link add/delete). */
const GANTT_SERVER_WRITE_DEBOUNCE_MS = 120;
/** DHTMLX clips the date header if this is smaller than the stacked scale rows + padding. */
const GANTT_SCALE_HEIGHT_TWO_ROW = 68;
const GANTT_SCALE_HEIGHT_SINGLE_ROW = 36;

function scaleHeightForViewMode(mode: ViewMode): number {
    return mode === 'day' ? GANTT_SCALE_HEIGHT_SINGLE_ROW : GANTT_SCALE_HEIGHT_TWO_ROW;
}

/** DHTMLX places markers on the column start; shift by half column so the line matches centered day labels. */
function todayMarkerHorizontalShiftPx(mode: ViewMode): number {
    const columnWidth = mode === 'month' ? GANTT_MONTH_VIEW_COLUMN_WIDTH : GANTT_DEFAULT_COLUMN_WIDTH;
    return columnWidth / 2 - 1;
}

function computeGanttDataTimelineBounds(tasks: ProjectTask[], getProjectStart: () => Date): { start: Date; end: Date } {
    const projectStart = startOfLocalDay(getProjectStart());
    let minMs = projectStart.getTime();
    let maxMs = projectStart.getTime() + GANTT_MIN_EMPTY_SPAN_DAYS * 86400000;

    for (const task of tasks) {
        const sd = new Date(task.start_date);
        if (Number.isNaN(sd.getTime())) {
            continue;
        }
        const dur = Math.max(1, Number(task.duration_days) || 1);
        const ed = new Date(sd);
        ed.setDate(ed.getDate() + dur);
        minMs = Math.min(minMs, sd.getTime());
        maxMs = Math.max(maxMs, ed.getTime());
    }

    const start = startOfLocalDay(new Date(minMs));
    start.setDate(start.getDate() - GANTT_DATA_PAD_BEFORE_DAYS);
    const end = startOfLocalDay(new Date(maxMs));
    end.setDate(end.getDate() + GANTT_DATA_PAD_AFTER_DAYS);
    end.setHours(23, 59, 59, 999);

    return { start, end };
}

function getGanttScrollAnchor(mode: ViewMode, getProjectStart: () => Date): Date {
    if (mode === 'month') {
        return new Date();
    }
    if (mode === 'week') {
        return startOfIsoWeekMonday(new Date());
    }
    if (mode === 'day') {
        const today = startOfLocalDay(new Date());
        const center = new Date(today);
        center.setDate(center.getDate() + Math.floor(GANTT_DAY_VIEW_FOCUS_FUTURE_DAYS / 2));
        return center;
    }

    return getProjectStart();
}

/**
 * Timeline always spans all task dates (plus padding) so tasks are never clipped.
 * Column width follows the active scale (month uses denser columns).
 */
function applyGanttTimelineRange(chart: DhtmlxGanttInstance, mode: ViewMode, getProjectStart: () => Date, taskList: ProjectTask[]): void {
    const { start, end } = computeGanttDataTimelineBounds(taskList, getProjectStart);
    chart.config.start_date = start;
    chart.config.end_date = end;
    chart.config.column_width = mode === 'month' ? GANTT_MONTH_VIEW_COLUMN_WIDTH : GANTT_DEFAULT_COLUMN_WIDTH;
}

interface GanttScaleRow {
    unit: string;
    step: number;
    format: string | ((date: Date) => string);
    css?: (date: Date) => string;
}

function ganttScaleCellCss(step: 'day' | 'week' | 'month', spanMonths?: number): (date: Date) => string {
    return (date: Date) => buildGanttTimelineCellClasses(date, new Date(), { cellStep: step, spanMonths });
}

function getIsoWeek(date: Date): number {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = utc.getUTCDay() || 7;
    utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
    return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function buildGanttScales(mode: ViewMode, locale: string, weekPrefix: string): GanttScaleRow[] {
    const tag = locale === 'lt' ? 'lt-LT' : 'en-GB';
    const monthYear = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric' });
    const monthLong = new Intl.DateTimeFormat(tag, { month: 'long' });
    const dayNumber = new Intl.DateTimeFormat(tag, { day: 'numeric' });
    const monthNumber = new Intl.DateTimeFormat(tag, { month: 'numeric' });

    switch (mode) {
        case 'day':
            return [{ unit: 'day', step: 1, format: (d) => dayNumber.format(d), css: ganttScaleCellCss('day') }];
        case 'week':
            return [
                { unit: 'week', step: 1, format: (d) => `${weekPrefix} ${getIsoWeek(d)}`, css: ganttScaleCellCss('week') },
                { unit: 'day', step: 1, format: (d) => dayNumber.format(d), css: ganttScaleCellCss('day') },
            ];
        case 'month':
            return [
                { unit: 'month', step: 1, format: (d) => monthYear.format(d), css: ganttScaleCellCss('month') },
                { unit: 'day', step: 1, format: (d) => dayNumber.format(d), css: ganttScaleCellCss('day') },
            ];
        case 'quarter':
            return [
                { unit: 'month', step: 3, format: (d) => monthLong.format(d), css: ganttScaleCellCss('month', 3) },
                { unit: 'month', step: 1, format: (d) => monthNumber.format(d), css: ganttScaleCellCss('month') },
            ];
        default:
            return [{ unit: 'month', step: 1, format: (d) => monthYear.format(d), css: ganttScaleCellCss('month') }];
    }
}

function taskStatusSwatchClass(color: string): string {
    const token = color
        .trim()
        .split(/\s+/)
        .find((c) => c.startsWith('bg-'));
    return token ?? 'bg-muted';
}

function escapeHtml(value: string): string {
    return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

const GANTT_GRID_COLUMN_HEAD_ICON_SVG =
    '<svg class="gantt-grid-head-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/></svg>';

function buildGanttGridColumnLabel(plainLabel: string): string {
    return `<span class="gantt-grid-head-label">${GANTT_GRID_COLUMN_HEAD_ICON_SVG}<span class="gantt-grid-head-text">${escapeHtml(plainLabel)}</span></span>`;
}

function buildGanttGridCompactColumnLabel(plainLabel: string): string {
    return `<span class="gantt-grid-head-label gantt-grid-head-label--compact"><span class="gantt-grid-head-text">${escapeHtml(plainLabel)}</span></span>`;
}

function formatGanttGridIsoYmd(ymd: string): string {
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : '';
}

function dayDiffInclusiveFloor(left: Date, right: Date): number {
    return Math.floor((left.getTime() - right.getTime()) / 86400000);
}

function isCompletedTaskStatus(status: unknown): boolean {
    const normalized = String(status ?? '')
        .trim()
        .toLowerCase()
        .replaceAll('_', '-')
        .replaceAll(' ', '-');
    return normalized === 'completed';
}

function timelineHealthColorHex(health: TimelineHealth): string {
    if (health === 'late') {
        return '#DC2626';
    }
    if (health === 'warning') {
        return '#EAB308';
    }
    return '#16A34A';
}

function buildTimelineHealthIconHtml(health: TimelineHealth): string {
    const color = timelineHealthColorHex(health);
    if (health === 'ok') {
        return `<span class="gantt-grid-status-icon gantt-grid-status-icon--ok" aria-hidden="true"><svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="${color}" stroke-width="2"></circle><path d="M6 10.5L8.5 13L14 7.5" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
    }

    return `<span class="gantt-grid-status-icon gantt-grid-status-icon--${health}" aria-hidden="true"><svg viewBox="0 0 20 20" fill="none"><path d="M10 3L18 17H2L10 3Z" stroke="${color}" stroke-width="2" stroke-linejoin="round"></path><path d="M10 7V11" stroke="${color}" stroke-width="2" stroke-linecap="round"></path><circle cx="10" cy="14" r="1.2" fill="${color}"></circle></svg></span>`;
}

function coerceGanttTaskDateToYmd(value: unknown): string {
    if (value == null) {
        return '';
    }
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return '';
        }
        return formatLocalDateYmd(value);
    }
    const s = String(value).trim();
    const isoDay = /^(\d{4}-\d{2}-\d{2})/.exec(s);
    if (isoDay) {
        return isoDay[1];
    }
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) {
        return formatLocalDateYmd(parsed);
    }
    return '';
}

function resolveGanttGridEndYmd(task: GanttGridTemplateTask): string {
    const startYmd = coerceGanttTaskDateToYmd(task.start_date);
    const endYmd = coerceGanttTaskDateToYmd(task.end_date);
    if (startYmd && endYmd) {
        const normalized = normalizeInclusiveEndDate(startYmd, endYmd, task.duration);
        return normalized;
    }

    if (!startYmd || task.duration == null || Number.isNaN(Number(task.duration))) {
        return '';
    }
    const dur = Math.max(0, Math.floor(Number(task.duration)));
    const start = parseLocalCalendarDate(startYmd);
    if (dur <= 0) {
        return formatLocalDateYmd(start);
    }
    const end = new Date(start);
    end.setDate(end.getDate() + dur - 1);
    return formatLocalDateYmd(end);
}

function normalizeInclusiveEndDate(startYmd: string, endYmd: string, duration?: number): string {
    const start = parseLocalCalendarDate(startYmd);
    const end = parseLocalCalendarDate(endYmd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return endYmd;
    }

    const durationNumber = Number(duration);
    if (!Number.isFinite(durationNumber) || durationNumber < 1) {
        return endYmd;
    }

    const roundedDuration = Math.max(1, Math.round(durationNumber));
    const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000);

    // DHTMLX often keeps `end_date` as an exclusive boundary for day-scale tasks.
    // If diff equals duration, convert to inclusive day by subtracting one.
    if (diffDays === roundedDuration) {
        const inclusiveEnd = new Date(end);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        return formatLocalDateYmd(inclusiveEnd);
    }

    return endYmd;
}

function normalizeDraggedTaskDates(task: { start_date: string | Date; end_date?: string | Date; duration?: number }): {
    startYmd: string;
    endYmd: string;
} {
    const startDate = parseLocalCalendarDate(task.start_date);
    if (Number.isNaN(startDate.getTime())) {
        return { startYmd: '', endYmd: '' };
    }

    const endRaw = task.end_date ? parseLocalCalendarDate(task.end_date) : null;
    if (endRaw && !Number.isNaN(endRaw.getTime())) {
        const startYmd = formatLocalDateYmd(startDate);
        const endYmd = formatLocalDateYmd(endRaw);
        return {
            startYmd,
            endYmd: normalizeInclusiveEndDate(startYmd, endYmd, task.duration),
        };
    }

    const duration = Math.max(1, Math.floor(Number(task.duration ?? 1)));
    const fallbackEnd = new Date(startDate);
    fallbackEnd.setDate(fallbackEnd.getDate() + duration - 1);

    return {
        startYmd: formatLocalDateYmd(startDate),
        endYmd: formatLocalDateYmd(fallbackEnd),
    };
}

export default function GanttChart({ tasks, projectId, projectOwnerId, users, departmentSortActive = false, onToggleDepartmentSort }: Props) {
    const { t } = useTranslations();
    const page = usePage<GanttPageProps>();
    const appLocale = page.props.locale ?? 'en';
    const authAbilities = page.props.auth?.abilities ?? [];
    const authRoles = page.props.auth?.roles ?? [];
    const currentUserId = page.props.auth?.user?.id ?? null;
    // The project owner may delete tasks in any department and move the project
    // timeline, even without the usual task permissions/roles.
    const isProjectOwner = Boolean(currentUserId && projectOwnerId && currentUserId === projectOwnerId);
    const canUpdateAnyTask = authAbilities.includes('tasks.update') || authRoles.includes('Administrator');
    const canCreateTask = authAbilities.includes('tasks.create') || authRoles.includes('Administrator');
    // The owner can open a task to delete it even when they cannot edit it.
    const canOpenTaskModal = canUpdateAnyTask || isProjectOwner;
    // Only administrators, sales managers, and the project owner can move the
    // project timeline or open its edit modal.
    const canManageProjectTimeline = authRoles.includes('Administrator') || authRoles.includes('Sales Manager') || isProjectOwner;
    const projectTimelineLabel = t('projects.gantt.project_timeline', 'Project timeline');
    const { settings } = useSettings();
    const projectTaskStatuses = useMemo(() => settings.projectTaskStatuses ?? [], [settings.projectTaskStatuses]);
    const translateStatusLabel = useCallback(
        (value: string, fallback: string) => t(`projects.gantt.status.${String(value).replace(/-/g, '_')}`, fallback),
        [t],
    );
    const roleLtMap = useMemo(() => {
        const map = new Map<string, string>();
        (settings.projectRoleTranslations ?? []).forEach((item) => {
            if (item?.value && item?.label_lt) {
                map.set(String(item.value), String(item.label_lt));
            }
        });
        return map;
    }, [settings.projectRoleTranslations]);
    const departmentLabelByKey = useMemo(() => {
        const map = new Map<string, string>();
        (settings.availableRoles ?? []).forEach((roleName) => {
            const label = appLocale === 'lt' ? (roleLtMap.get(roleName) ?? roleName) : roleName;
            map.set(`role:${roleName}`, label);
        });
        (settings.projectTaskDepartments ?? []).forEach((department) => {
            if (department?.value && department?.label) {
                map.set(String(department.value), String(department.label));
            }
        });
        return map;
    }, [settings.availableRoles, settings.projectTaskDepartments, appLocale, roleLtMap]);
    const [localTaskVersion, setLocalTaskVersion] = useState(0);
    const timelineHealthByTaskId = useMemo(() => {
        const map = new Map<string, TimelineHealth>();
        const timelineTasks = tasks.filter((task) => task.is_project_timeline);
        const workTasks = tasks.filter((task) => !task.is_project_timeline && !isCompletedTaskStatus(task.status));

        timelineTasks.forEach((timelineTask) => {
            const timelineStart = parseLocalCalendarDate(timelineTask.start_date);
            const timelineEnd = parseLocalCalendarDate(timelineTask.end_date);

            const normalizedStart = timelineStart <= timelineEnd ? timelineStart : timelineEnd;
            const normalizedEnd = timelineStart <= timelineEnd ? timelineEnd : timelineStart;

            const validWorkTasks = workTasks
                .map((task) => ({ task, start: parseLocalCalendarDate(task.start_date), end: parseLocalCalendarDate(task.end_date) }))
                .filter((entry) => !Number.isNaN(entry.start.getTime()) && !Number.isNaN(entry.end.getTime()));

            const anyLate = validWorkTasks.some((entry) => entry.end.getTime() > normalizedEnd.getTime());
            if (anyLate) {
                map.set(timelineTask.id, 'late');
                return;
            }

            const allInsideRange = validWorkTasks.every(
                (entry) => entry.start.getTime() >= normalizedStart.getTime() && entry.end.getTime() <= normalizedEnd.getTime(),
            );

            if (allInsideRange && validWorkTasks.length > 0) {
                const closestEnd = validWorkTasks.reduce(
                    (current, entry) => (entry.end.getTime() > current.getTime() ? entry.end : current),
                    validWorkTasks[0].end,
                );
                const daysUntilDeadline = dayDiffInclusiveFloor(normalizedEnd, closestEnd);
                if (daysUntilDeadline <= 7) {
                    map.set(timelineTask.id, 'warning');
                    return;
                }
                map.set(timelineTask.id, 'ok');
                return;
            }

            map.set(timelineTask.id, 'ok');
        });

        return map;
    }, [tasks, localTaskVersion]);
    const resolveTaskDepartmentLabel = useCallback(
        (task: { department_key?: string; department_label?: string }) => {
            const direct = String(task.department_label ?? '').trim();
            if (direct.length > 0) {
                return direct;
            }
            const key = String(task.department_key ?? '').trim();
            if (key.length === 0) {
                return '';
            }
            return departmentLabelByKey.get(key) ?? key;
        },
        [departmentLabelByKey],
    );
    const buildTaskBarTextHtml = useCallback(
        (task: { text?: string; type?: string; department_key?: string; department_label?: string; is_project_timeline?: boolean }) => {
            if (task.is_project_timeline) {
                return '';
            }
            const title = escapeHtml(String(task.text ?? ''));
            if (task.type === 'project') {
                return `<span class="gantt-task-inline gantt-task-inline--project"><span class="gantt-task-title">${title}</span></span>`;
            }

            const departmentLabel = escapeHtml(resolveTaskDepartmentLabel(task));

            const parts = [`<span class="gantt-task-title">${title}</span>`];
            if (departmentLabel.length > 0) {
                parts.push('<span class="gantt-task-sep" aria-hidden="true">·</span>');
                parts.push(`<span class="gantt-task-department">${departmentLabel}</span>`);
            }

            return `<span class="gantt-task-inline">${parts.join('')}</span>`;
        },
        [resolveTaskDepartmentLabel],
    );
    const ganttStatusThemeRules = useMemo(
        () =>
            buildGanttTaskStatusCssRules(['.gantt-container .gantt_container', '.gantt-container-fullscreen .gantt_container'], projectTaskStatuses),
        [projectTaskStatuses],
    );
    const buildGridTreeCellHtml = useCallback(
        (task: {
            id?: string;
            text?: string;
            type?: string;
            status?: string;
            department_key?: string;
            department_label?: string;
            parent?: string;
            is_project_timeline?: boolean;
        }) => {
            const isProject = task.type === 'project';
            const isTimeline = Boolean(task.is_project_timeline);
            const isChildTask = !isProject && Boolean(task.parent && String(task.parent).trim() !== '');
            const statusSlug = isProject ? '' : ganttStatusClassSlug(String(task.status ?? 'not-started'));
            const bubbleColor = resolveTaskStatusSwatchFromSettings(
                { status: String(task.status ?? ''), type: isProject ? 'project' : 'task' },
                projectTaskStatuses,
            );
            const title = escapeHtml(String(task.text ?? ''));
            const departmentLabel = !isProject ? escapeHtml(resolveTaskDepartmentLabel(task)) : '';
            const health = isTimeline ? (timelineHealthByTaskId.get(String(task.id ?? '')) ?? 'ok') : null;
            const rowClass = isProject
                ? 'gantt-grid-task-cell gantt-grid-task-cell--project'
                : `gantt-grid-task-cell ${statusSlug}${isChildTask ? ' gantt-grid-task-cell--child' : ''}${isTimeline ? ' gantt-grid-task-cell--timeline' : ''}`;
            const bubble = isTimeline
                ? buildTimelineHealthIconHtml(health ?? 'ok')
                : `<span class="gantt-grid-status-bubble${isChildTask ? ' gantt-grid-status-bubble--child' : ''}" style="background-color: ${bubbleColor};" aria-hidden="true"></span>`;
            if (isProject) {
                return `<div class="${rowClass}">${bubble}<div class="gantt-grid-task-text"><div class="gantt-grid-task-name">${title}</div></div></div>`;
            }
            const departmentLine = departmentLabel.length > 0 ? `<span class="gantt-grid-task-department-text">${departmentLabel}</span>` : '';

            return `<div class="${rowClass}">${bubble}<div class="gantt-grid-task-text"><span class="gantt-grid-task-name-wrap"><span class="gantt-grid-task-name">${title}</span></span><div class="gantt-grid-task-meta">${departmentLine}</div></div></div>`;
        },
        [projectTaskStatuses, resolveTaskDepartmentLabel, timelineHealthByTaskId],
    );
    const buildGanttGridActionCellHtml = useCallback(
        (task: GanttGridTemplateTask) => {
            if (!canCreateTask || task.type === 'project' || task.is_project_timeline) {
                return '';
            }
            const parentTaskId = escapeHtml(String(task.id ?? ''));
            return `<button type="button" class="gantt-grid-add-task-btn" data-parent-task-id="${parentTaskId}" aria-label="Add child task">+</button>`;
        },
        [canCreateTask],
    );
    const buildGanttGridDateCellHtml = useCallback((task: GanttGridTemplateTask, field: 'start' | 'end') => {
        if (task.type === 'project') {
            return '<span class="gantt-grid-date-cell gantt-grid-date-cell--muted" aria-hidden="true">—</span>';
        }
        const ymd = field === 'start' ? coerceGanttTaskDateToYmd(task.start_date) : resolveGanttGridEndYmd(task);
        const formatted = ymd.length > 0 ? formatGanttGridIsoYmd(ymd) : '';
        const display = formatted.length > 0 ? formatted : '—';
        return `<span class="gantt-grid-date-cell">${escapeHtml(display)}</span>`;
    }, []);
    const buildGanttGridColumnsConfig = useCallback(
        (includeDateColumns: boolean) => {
            const taskNameLabel = buildGanttGridColumnLabel(t('projects.gantt.columns.task_name', 'Task name'));
            const treeCol = {
                name: 'text',
                label: taskNameLabel,
                width: GANTT_GRID_TREE_COL_WIDTH,
                tree: true,
                template: (task: GanttGridTemplateTask) => buildGridTreeCellHtml(task),
            };
            const actionCol = {
                name: 'grid_action',
                label: '',
                width: GANTT_GRID_ACTION_COL_WIDTH,
                align: 'center',
                template: (task: GanttGridTemplateTask) => buildGanttGridActionCellHtml(task),
            };
            if (!includeDateColumns) {
                return [treeCol, actionCol];
            }
            return [
                treeCol,
                {
                    name: 'grid_start',
                    label: buildGanttGridCompactColumnLabel(t('projects.gantt.columns.start', 'Start')),
                    width: GANTT_GRID_START_DATE_COL_WIDTH,
                    align: 'center',
                    template: (task: GanttGridTemplateTask) => buildGanttGridDateCellHtml(task, 'start'),
                },
                {
                    name: 'grid_end',
                    label: buildGanttGridCompactColumnLabel(t('projects.gantt.columns.end', 'End')),
                    width: GANTT_GRID_END_DATE_COL_WIDTH,
                    align: 'center',
                    template: (task: GanttGridTemplateTask) => buildGanttGridDateCellHtml(task, 'end'),
                },
                actionCol,
            ];
        },
        [t, buildGridTreeCellHtml, buildGanttGridDateCellHtml, buildGanttGridActionCellHtml],
    );
    const ganttContainer = useRef<HTMLDivElement>(null);
    const fullscreenGanttContainer = useRef<HTMLDivElement>(null);
    const ganttInstanceFactory = useMemo(() => getGanttInstanceFactory(), []);
    const ganttSingleton = useMemo(() => getGanttSingleton(), []);
    const chartRef = useRef<DhtmlxGanttInstance | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
    const [createTaskParentId, setCreateTaskParentId] = useState<string | null>(null);
    const currentViewMode: ViewMode = 'month';
    const currentViewModeRef = useRef<ViewMode>(currentViewMode);
    const todayMarkerShiftPx = useMemo(() => todayMarkerHorizontalShiftPx(currentViewMode), []);
    const ganttMarkerShiftStyle = useMemo((): CSSProperties => {
        return { ['--gantt-today-marker-shift' as string]: `${todayMarkerShiftPx}px` };
    }, [todayMarkerShiftPx]);
    const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<ProjectTask | null>(null);
    const [timelineReasonModalOpen, setTimelineReasonModalOpen] = useState(false);
    const [timelineMoveReason, setTimelineMoveReason] = useState('');
    const [pendingTimelineMove, setPendingTimelineMove] = useState<PendingTimelineMove | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showGridDateColumns, setShowGridDateColumns] = useState(false);
    const showGridDateColumnsRef = useRef(showGridDateColumns);
    showGridDateColumnsRef.current = showGridDateColumns;
    const applyGridDateColumnsToChartRef = useRef<(visible: boolean) => void>(() => {});
    const [zoomIndex, setZoomIndex] = useState(GANTT_ZOOM_DEFAULT_INDEX);
    const zoomIndexRef = useRef(zoomIndex);
    zoomIndexRef.current = zoomIndex;
    const [statusContextMenu, setStatusContextMenu] = useState<{
        taskId: string;
        clientX: number;
        clientY: number;
    } | null>(null);
    const [statusMenuPosition, setStatusMenuPosition] = useState<{ left: number; top: number } | null>(null);
    const statusMenuPanelRef = useRef<HTMLDivElement>(null);
    const tasksRef = useRef(tasks);
    useEffect(() => {
        tasksRef.current = tasks;
        setLocalTaskVersion((current) => current + 1);
    }, [tasks]);
    const projectIdRef = useRef(projectId);
    projectIdRef.current = projectId;
    const projectTimelineLabelRef = useRef(projectTimelineLabel);
    projectTimelineLabelRef.current = projectTimelineLabel;

    // Why a move is denied — drives a context-specific toast so the user knows
    // *who* may move the item. The rule itself is shared with the edit sheet and
    // mirrors the endpoint, so the UI never offers a move the server refuses.
    type GanttMoveDenialReason = TaskUpdateDenialReason;

    const ganttTaskMoveDenialReason = useCallback(
        (task: ProjectTask | null | undefined): GanttMoveDenialReason | null =>
            taskUpdateDenialReason(task, { roles: authRoles, abilities: authAbilities, userId: currentUserId, isProjectOwner }),
        [authAbilities, authRoles, currentUserId, isProjectOwner],
    );

    const canUpdateGanttTask = useCallback(
        (task: ProjectTask | null | undefined): boolean => ganttTaskMoveDenialReason(task) === null,
        [ganttTaskMoveDenialReason],
    );

    const ganttTaskMoveDenialReasonById = useCallback(
        (taskId: string | number): GanttMoveDenialReason | null =>
            ganttTaskMoveDenialReason(tasksRef.current.find((task) => task.id === String(taskId))),
        [ganttTaskMoveDenialReason],
    );

    const canUpdateGanttTaskById = useCallback(
        (taskId: string | number): boolean => canUpdateGanttTask(tasksRef.current.find((task) => task.id === String(taskId))),
        [canUpdateGanttTask],
    );

    // Explain a denied drag. A fixed toast id collapses repeated attempts into one
    // toast instead of stacking a new warning on every drag gesture.
    const notifyGanttMoveDenied = useCallback(
        (reason: GanttMoveDenialReason) => {
            const message =
                reason === 'timeline'
                    ? t('projects.gantt.messages.timeline_move_not_allowed', 'Only the owner and sales department have permission to move this.')
                    : reason === 'department'
                      ? t(
                            'projects.gantt.messages.task_move_department_restricted',
                            'This task belongs to another department. Only that department or the person it is assigned to can move it.',
                        )
                      : t('projects.gantt.messages.task_update_not_allowed', 'You do not have permission to move this task');
            toast.warning(message, { id: 'gantt-task-move-denied' });
        },
        [t],
    );

    const patchCachedTask = useCallback((taskId: string, patch: Partial<ProjectTask>) => {
        const row = tasksRef.current.find((r) => r.id === taskId);
        if (row) {
            Object.assign(row, patch);
            setLocalTaskVersion((current) => current + 1);
            const chart = chartRef.current;
            if (chart) {
                chart.render();
            }
        }
    }, []);

    const patchChartTask = useCallback((taskId: string, patch: Partial<ProjectTask>) => {
        const chart = chartRef.current;
        if (!chart) {
            return;
        }

        try {
            const task = chart.getTask(taskId) as Record<string, unknown> | null;
            if (!task) {
                return;
            }
            Object.assign(task, patch);
            chart.refreshTask(taskId);
            chart.render();
        } catch {
            //
        }
    }, []);

    type RowMoveContext = { movedId: string; oldParent: string | number | undefined };
    const rowMoveContextRef = useRef<RowMoveContext | null>(null);
    const timelineDragSaveTimerRef = useRef<number | null>(null);
    const preservedScrollStateRef = useRef<GanttScrollState | null>(null);
    const linkRelationshipAddTimersRef = useRef<Map<string, number>>(new Map());
    const linkRelationshipDeleteTimersRef = useRef<Map<string, number>>(new Map());

    const captureScrollState = useCallback((chart: DhtmlxGanttInstance) => {
        if (typeof chart.getScrollState !== 'function') {
            return;
        }
        const state = chart.getScrollState() as { x?: number; y?: number } | null;
        if (state && typeof state.x === 'number' && typeof state.y === 'number') {
            preservedScrollStateRef.current = { x: state.x, y: state.y };
        }
    }, []);

    const restoreScrollState = useCallback((chart: DhtmlxGanttInstance): boolean => {
        if (typeof chart.scrollTo !== 'function') {
            return false;
        }
        const state = preservedScrollStateRef.current;
        if (!state) {
            return false;
        }
        chart.scrollTo(state.x, state.y);
        return true;
    }, []);

    const getProjectStartDate = useCallback(() => {
        if (!tasks.length) {
            return new Date();
        }

        const dates = tasks.map((task) => new Date(task.start_date)).filter((date) => !isNaN(date.getTime()));
        return dates.length ? new Date(Math.min(...dates.map((d) => d.getTime()))) : new Date();
    }, [tasks]);

    const syncTodayMarker = useCallback(() => {
        const chart = chartRef.current;
        if (!chart) {
            return;
        }
        const at = startOfLocalDay(new Date());
        if (typeof chart.getMarker === 'function' && chart.getMarker(GANTT_TODAY_MARKER_ID)) {
            chart.deleteMarker(GANTT_TODAY_MARKER_ID);
        }
        chart.addMarker({
            id: GANTT_TODAY_MARKER_ID,
            start_date: at,
            css: 'gantt_marker_today_line',
            title: t('projects.gantt.today_marker_title', 'Today'),
            text: '',
        });
        if (typeof chart.renderMarkers === 'function') {
            chart.renderMarkers();
        }
    }, [t]);

    const syncGanttRowMoveToServer = useCallback(
        (chart: DhtmlxGanttInstance, movedId: string, newParentRaw: string | number | undefined) => {
            void (async () => {
                const ctx = rowMoveContextRef.current;
                rowMoveContextRef.current = null;

                const oldParentRaw = ctx?.movedId === movedId ? ctx.oldParent : undefined;
                const oldKey = resolveGanttBranchKey(chart, oldParentRaw);
                const newKey = resolveGanttBranchKey(chart, newParentRaw);

                const revert = () => {
                    chart.clearAll();
                    const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasksRef.current, projectTimelineLabelRef.current);
                    chart.parse({
                        data: ganttTasks,
                        links: ganttLinks,
                    });
                    chart.eachTask(function (task: { id: string }) {
                        if (chart.hasChild(task.id)) {
                            chart.open(task.id);
                        }
                    });
                    syncTodayMarker();
                };

                const fail = () => {
                    toast.error(t('projects.gantt.messages.task_order_save_failed', 'Failed to save task order'));
                    revert();
                };

                const successToast = () => {
                    toast.success(t('projects.gantt.messages.task_order_saved', 'Task order saved'));
                };

                const payloadNew = buildReorderPayloadFromBranch(chart, newParentRaw);
                if (!payloadNew) {
                    return;
                }

                if (chartTaskIsProjectTimeline(chart, movedId) || branchHasMovedProjectTimeline(chart, newParentRaw)) {
                    toast.error(t('projects.gantt.messages.timeline_pinned', 'Project timeline must stay at the top of the Gantt chart'));
                    revert();
                    return;
                }

                try {
                    if (branchKeysEqual(oldKey, newKey)) {
                        await laravelJsonFetch(route('projects.tasks.reorder', projectIdRef.current), 'POST', payloadNew);
                        payloadNew.tasks.forEach((row) => {
                            patchCachedTask(row.id, { sort_order: row.sort_order });
                        });
                        successToast();
                        return;
                    }

                    const parentUuid = ganttParentRawToApiParentTaskId(newParentRaw);
                    const sortIndex = payloadNew.tasks.findIndex((row) => row.id === movedId);
                    const safeSort = sortIndex >= 0 ? sortIndex : 0;

                    await laravelJsonFetch(route('tasks.update', movedId), 'PUT', {
                        parent_task_id: parentUuid,
                        sort_order: safeSort,
                    });
                    await laravelJsonFetch(route('projects.tasks.reorder', projectIdRef.current), 'POST', payloadNew);

                    const payloadOld = buildReorderPayloadFromBranch(chart, oldParentRaw);
                    if (payloadOld && !branchKeysEqual(oldKey, newKey)) {
                        await laravelJsonFetch(route('projects.tasks.reorder', projectIdRef.current), 'POST', payloadOld);
                        payloadOld.tasks.forEach((row) => {
                            patchCachedTask(row.id, { sort_order: row.sort_order });
                        });
                    }

                    patchCachedTask(movedId, {
                        parent_task_id: parentUuid,
                        sort_order: safeSort,
                    });
                    payloadNew.tasks.forEach((row) => {
                        patchCachedTask(row.id, { sort_order: row.sort_order });
                    });
                    successToast();
                } catch {
                    fail();
                }
            })();
        },
        [patchCachedTask, syncTodayMarker, t],
    );

    useLayoutEffect(() => {
        applyGridDateColumnsToChartRef.current = (visible: boolean) => {
            const chart = chartRef.current;
            if (!chart) {
                return;
            }
            chart.config.columns = buildGanttGridColumnsConfig(visible);
            chart.config.grid_width = visible ? GANTT_GRID_WIDTH : GANTT_GRID_TREE_COL_WIDTH + GANTT_GRID_ACTION_COL_WIDTH;
            chart.render();
            if (typeof chart.setSizes === 'function') {
                chart.setSizes();
            }
            syncTodayMarker();
        };
    }, [buildGanttGridColumnsConfig, syncTodayMarker]);

    const toggleGridDateColumnsVisibility = useCallback(() => {
        setShowGridDateColumns((prev) => {
            const next = !prev;
            showGridDateColumnsRef.current = next;
            applyGridDateColumnsToChartRef.current(next);
            return next;
        });
    }, []);

    const applyZoom = useCallback((index: number) => {
        const chart = chartRef.current;
        if (!chart) return;
        const colWidth = GANTT_ZOOM_LEVELS[index];
        chart.config.column_width = colWidth;
        chart.config.min_column_width = colWidth;
        chart.render();
    }, []);

    const zoomIn = useCallback(() => {
        setZoomIndex((prev) => {
            const next = Math.min(prev + 1, GANTT_ZOOM_LEVELS.length - 1);
            applyZoom(next);
            return next;
        });
    }, [applyZoom]);

    const zoomOut = useCallback(() => {
        setZoomIndex((prev) => {
            const next = Math.max(prev - 1, 0);
            applyZoom(next);
            return next;
        });
    }, [applyZoom]);

    useLayoutEffect(() => {
        if (!statusContextMenu) {
            setStatusMenuPosition(null);
            return;
        }
        const pad = 8;
        const w = window.innerWidth;
        const h = window.innerHeight;
        let left = statusContextMenu.clientX;
        let top = statusContextMenu.clientY;
        const panel = statusMenuPanelRef.current;
        if (panel) {
            const rect = panel.getBoundingClientRect();
            if (left + rect.width > w - pad) {
                left = Math.max(pad, w - rect.width - pad);
            }
            if (top + rect.height > h - pad) {
                top = Math.max(pad, h - rect.height - pad);
            }
            if (left < pad) {
                left = pad;
            }
            if (top < pad) {
                top = pad;
            }
        }
        setStatusMenuPosition({ left, top });
    }, [statusContextMenu, projectTaskStatuses.length]);

    useEffect(() => {
        if (!statusContextMenu) {
            return;
        }
        const close = () => {
            setStatusContextMenu(null);
            setStatusMenuPosition(null);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                close();
            }
        };
        const onPointerDown = (e: PointerEvent) => {
            if (statusMenuPanelRef.current?.contains(e.target as Node)) {
                return;
            }
            close();
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [statusContextMenu]);

    const showTodayRange = () => {
        const chart = chartRef.current;
        if (!chart) {
            return;
        }
        const anchor = startOfLocalDay(new Date());
        chart.showDate(anchor);
        syncTodayMarker();
    };

    // Fullscreen functionality
    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    // Handle escape key to exit fullscreen
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        if (isFullscreen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isFullscreen]);

    // Initialize gantt in fullscreen mode (inline chart is fully configured in the main effect below)
    useEffect(() => {
        if (!isFullscreen || !fullscreenGanttContainer.current) {
            return;
        }

        const weekPrefix = t('projects.gantt.scales.week_prefix', 'Week');
        let fullscreenRowDragStartId: string | number | undefined;
        let fullscreenRowDragEndId: string | number | undefined;
        let fullscreenBeforeTaskDragId: string | number | undefined;
        let cancelled = false;
        const timeoutId = window.setTimeout(() => {
            if (cancelled || !fullscreenGanttContainer.current) {
                return;
            }

            const chart = ganttInstanceFactory?.() ?? ganttSingleton;
            chartRef.current = chart;

            chart.plugins({ marker: true });
            chart.config.show_tasks_outside_timescale = true;
            chart.config.date_format = '%Y-%m-%d';
            chart.config.timeline_placeholder = {};
            chart.config.scales = buildGanttScales(currentViewMode, appLocale, weekPrefix);
            chart.config.scale_height = scaleHeightForViewMode(currentViewMode);
            applyGanttTimelineRange(chart, currentViewMode, getProjectStartDate, tasks);
            chart.config.columns = buildGanttGridColumnsConfig(showGridDateColumnsRef.current);
            chart.config.work_time = true;
            chart.config.skip_off_time = false;
            chart.config.highlight_critical_path = true;
            chart.config.auto_types = true;
            chart.config.show_progress = true;
            chart.config.drag_progress = false;
            chart.config.show_links = true;
            chart.config.readonly = false;
            chart.config.drag_links = true;
            chart.config.drag_move = true;
            chart.config.drag_resize = true;
            chart.config.order_branch = true;
            chart.config.order_branch_free = true;
            chart.config.open_tree_initially = true;
            chart.config.row_height = GANTT_ROW_HEIGHT;
            chart.config.bar_height = GANTT_BAR_HEIGHT;
            chart.config.min_column_width = GANTT_MONTH_VIEW_COLUMN_WIDTH;
            chart.config.grid_width = showGridDateColumnsRef.current ? GANTT_GRID_WIDTH : GANTT_GRID_TREE_COL_WIDTH + GANTT_GRID_ACTION_COL_WIDTH;
            chart.templates.task_class = (_start: Date, _end: Date, task: { id: string; status: string; is_project_timeline?: boolean }) => {
                const parts = [ganttStatusClassSlug(task.status)];
                if (task.is_project_timeline) {
                    parts.push('gantt_project_timeline');
                    parts.push(`gantt_project_timeline_${timelineHealthByTaskId.get(task.id) ?? 'ok'}`);
                }
                if (chart.hasChild(task.id)) {
                    parts.push('gantt_has_child');
                }

                return parts.join(' ');
            };
            chart.templates.task_text = (
                _start: Date,
                _end: Date,
                task: { text?: string; assigned_to?: string; type?: string; is_project_timeline?: boolean },
            ) => buildTaskBarTextHtml(task);
            chart.templates.timeline_cell_class = (_task: unknown, date: Date) =>
                buildGanttTimelineCellClasses(date, new Date(), {
                    cellStep: currentViewModeRef.current === 'quarter' ? 'month' : 'day',
                });
            chart.templates.scale_cell_class = () => '';
            chart.init(fullscreenGanttContainer.current);
            const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasks, projectTimelineLabel);
            chart.parse({
                data: ganttTasks,
                links: ganttLinks,
            });

            chart.eachTask(function (task: { id: string }) {
                if (chart.hasChild(task.id)) {
                    chart.open(task.id);
                }
            });

            fullscreenRowDragStartId = chart.attachEvent('onRowDragStart', function (id: string | number) {
                if (chartTaskIsProjectTimeline(chart, id)) {
                    return false;
                }
                if (!chart.isTaskExists(id)) {
                    return true;
                }
                const task = chart.getTask(id);
                rowMoveContextRef.current = { movedId: String(id), oldParent: task.parent };
                return true;
            });

            fullscreenRowDragEndId = chart.attachEvent('onRowDragEnd', function (id: string | number) {
                if (!chart.isTaskExists(id)) {
                    return true;
                }
                const task = chart.getTask(id);
                syncGanttRowMoveToServer(chart, String(id), task.parent);
                return true;
            });

            fullscreenBeforeTaskDragId = chart.attachEvent('onBeforeTaskDrag', function (id: string | number) {
                return canUpdateGanttTaskById(id);
            });

            chart.attachEvent('onBeforeLinkAdd', function (_id: string, link: LinkData) {
                const sourceTask = tasksRef.current.find((task) => task.id === String(link.source));
                const targetTask = tasksRef.current.find((task) => task.id === String(link.target));
                if (sourceTask?.is_project_timeline || targetTask?.is_project_timeline) {
                    toast.error(
                        t('projects.gantt.messages.timeline_dependency_forbidden', 'Project timeline cannot have dependencies or relationships.'),
                    );
                    return false;
                }
                return true;
            });

            chart.showDate(getGanttScrollAnchor(currentViewMode, getProjectStartDate));
            chart.render();
            syncTodayMarker();
        }, 300);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
            const chart = chartRef.current;
            if (chart && fullscreenRowDragStartId !== undefined) {
                try {
                    chart.detachEvent(fullscreenRowDragStartId);
                } catch {
                    //
                }
            }
            if (chart && fullscreenRowDragEndId !== undefined) {
                try {
                    chart.detachEvent(fullscreenRowDragEndId);
                } catch {
                    //
                }
            }
            if (chart && fullscreenBeforeTaskDragId !== undefined) {
                try {
                    chart.detachEvent(fullscreenBeforeTaskDragId);
                } catch {
                    //
                }
            }
            if (chart) {
                try {
                    chart.clearAll();
                } catch {
                    //
                }
            }
            chartRef.current = null;
        };
    }, [
        isFullscreen,
        tasks,
        appLocale,
        t,
        getProjectStartDate,
        syncTodayMarker,
        syncGanttRowMoveToServer,
        buildTaskBarTextHtml,
        buildGanttGridColumnsConfig,
        ganttInstanceFactory,
        ganttSingleton,
        canUpdateGanttTaskById,
        projectTimelineLabel,
        timelineHealthByTaskId,
        captureScrollState,
        restoreScrollState,
    ]);

    // Search functionality - now works with sub-tasks
    const handleSearch = (term: string) => {
        setSearchTerm(term);
        const chart = chartRef.current;
        if (!chart) {
            return;
        }

        if (term.trim() === '') {
            // Clear filter by refreshing data
            const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasks, projectTimelineLabel);
            chart.clearAll();
            chart.parse({
                data: ganttTasks,
                links: ganttLinks,
            });
            syncTodayMarker();
            return;
        }

        // Filter tasks based on search term - include parent tasks if child matches
        const { tasks: allTasksRaw, links: allLinks } = transformTasksToGantt(tasks, projectTimelineLabel);
        const allTasks = allTasksRaw as unknown as Array<{ id: string; text: string; parent?: string }>;
        const matchingTaskIds = new Set<string>();

        // First pass: find direct matches
        allTasks.forEach((task) => {
            if (task.text.toLowerCase().includes(term.toLowerCase())) {
                matchingTaskIds.add(task.id);
            }
        });

        // Second pass: include parent tasks of matching children
        allTasks.forEach((task) => {
            if (matchingTaskIds.has(task.id) && task.parent) {
                let parentId: string | undefined = task.parent;
                while (parentId) {
                    matchingTaskIds.add(parentId);
                    const parentTask = allTasks.find((t) => t.id === parentId);
                    parentId = parentTask?.parent;
                }
            }
        });

        // Third pass: include children of matching parents
        allTasks.forEach((task) => {
            if (task.parent && matchingTaskIds.has(task.parent)) {
                matchingTaskIds.add(task.id);
            }
        });

        const filteredTasks = allTasks.filter((task) => matchingTaskIds.has(task.id));

        chart.clearAll();
        chart.parse({
            data: filteredTasks,
            links: allLinks.filter(
                (link) =>
                    filteredTasks.some((task) => String(task.id) === String(link.source)) &&
                    filteredTasks.some((task) => String(task.id) === String(link.target)),
            ),
        });
        syncTodayMarker();
    };

    useLayoutEffect(() => {
        if (isFullscreen) {
            return;
        }

        const mountEl = ganttContainer.current;
        if (!mountEl) {
            return;
        }

        const linkAddTimersForCleanup = linkRelationshipAddTimersRef.current;
        const linkDeleteTimersForCleanup = linkRelationshipDeleteTimersRef.current;

        const chart = ganttInstanceFactory?.() ?? ganttSingleton;
        chartRef.current = chart;

        const weekPrefix = t('projects.gantt.scales.week_prefix', 'Week');
        chart.plugins({ marker: true });
        chart.config.show_tasks_outside_timescale = true;
        // Basic configuration
        chart.config.date_format = '%Y-%m-%d';
        chart.config.scales = buildGanttScales(currentViewMode, appLocale, weekPrefix);
        chart.config.scale_height = scaleHeightForViewMode(currentViewMode);
        chart.config.timeline_placeholder = {};
        applyGanttTimelineRange(chart, currentViewMode, getProjectStartDate, tasks);

        // Layout and grid configuration
        chart.config.columns = buildGanttGridColumnsConfig(showGridDateColumnsRef.current);

        // Modern features
        chart.config.work_time = true;
        chart.config.skip_off_time = false;
        chart.config.highlight_critical_path = true;
        chart.config.auto_types = true;
        chart.config.show_progress = true;
        chart.config.drag_progress = false;
        chart.config.show_links = true;
        chart.config.readonly = false; // Enable editing
        chart.config.drag_links = true; // Enable link creation by dragging
        chart.config.drag_move = true; // Enable dragging
        chart.config.drag_resize = true; // Enable resizing
        chart.config.order_branch = true; // Grid row drag to reorder tasks
        chart.config.order_branch_free = true; // Allow nesting under another task (change parent)
        chart.config.open_tree_initially = true; // Expand all tasks by default
        chart.config.row_height = GANTT_ROW_HEIGHT;
        chart.config.bar_height = GANTT_BAR_HEIGHT;
        chart.config.min_column_width = GANTT_MONTH_VIEW_COLUMN_WIDTH;
        chart.config.grid_width = showGridDateColumnsRef.current ? GANTT_GRID_WIDTH : GANTT_GRID_TREE_COL_WIDTH + GANTT_GRID_ACTION_COL_WIDTH;
        chart.templates.task_class = (_start: Date, _end: Date, task: { id: string; status: string; is_project_timeline?: boolean }) => {
            const parts = [ganttStatusClassSlug(task.status)];
            if (task.is_project_timeline) {
                parts.push('gantt_project_timeline');
                parts.push(`gantt_project_timeline_${timelineHealthByTaskId.get(task.id) ?? 'ok'}`);
            }
            if (chart.hasChild(task.id)) {
                parts.push('gantt_has_child');
            }

            return parts.join(' ');
        };
        chart.templates.task_text = (
            _start: Date,
            _end: Date,
            task: { text?: string; assigned_to?: string; type?: string; is_project_timeline?: boolean },
        ) => buildTaskBarTextHtml(task);
        chart.templates.timeline_cell_class = (_task: unknown, date: Date) =>
            buildGanttTimelineCellClasses(date, new Date(), {
                cellStep: currentViewModeRef.current === 'quarter' ? 'month' : 'day',
            });
        chart.templates.scale_cell_class = () => '';

        // Double-click event for task editing modal
        chart.attachEvent('onTaskDblClick', function (id: string) {
            // Find the task from our tasks array
            const task = tasks.find((t) => t.id === id); // ID is already a string (UUID)
            if (!task) {
                return false;
            }
            // The project timeline may only be opened by administrators, sales
            // managers, and the project owner; everyone else gets an error.
            if (task.is_project_timeline) {
                if (!canManageProjectTimeline) {
                    toast.warning(
                        t(
                            'projects.gantt.messages.timeline_edit_not_allowed',
                            'Only administrators, sales managers, and the project owner can edit the project timeline.',
                        ),
                    );
                    return false;
                }
                setSelectedTaskForEdit(task);
                return false;
            }
            // Editors open the full edit modal. A project owner without edit
            // rights can still open a task to delete it.
            const canOpen = canUpdateAnyTask || isProjectOwner;
            if (!canOpen) {
                return false; // No permission: don't open the modal
            }
            setSelectedTaskForEdit(task);
            return false; // Prevent default lightbox
        });

        const contextMenuHandlerId = chart.attachEvent(
            'onContextMenu',
            function (taskId: string | number | null, linkId: string | number | null, e: MouseEvent) {
                if (linkId) {
                    return true;
                }
                if (taskId == null || taskId === '') {
                    return true;
                }
                if (!canUpdateAnyTask) {
                    return true; // No update permission: don't offer the status context menu
                }
                const id = String(taskId);
                const task = tasksRef.current.find((row) => row.id === id);
                if (!task) {
                    return true;
                }
                setStatusContextMenu({ taskId: id, clientX: e.clientX, clientY: e.clientY });
                setStatusMenuPosition({ left: e.clientX, top: e.clientY });
                return false;
            },
        );
        const taskClickHandlerId = chart.attachEvent('onTaskClick', function (_id: string | number, e: MouseEvent) {
            const target = e.target as HTMLElement | null;
            const addButton = target?.closest('.gantt-grid-add-task-btn') as HTMLElement | null;
            if (!addButton) {
                return true;
            }
            e.preventDefault();
            e.stopPropagation();
            if (!canCreateTask) {
                return false; // No create permission: ignore the add-task button
            }
            const parentTaskId = addButton.getAttribute('data-parent-task-id');
            setCreateTaskParentId(parentTaskId && parentTaskId.trim() !== '' ? parentTaskId : null);
            setCreateTaskModalOpen(true);
            return false;
        });

        const rowDragStartHandlerId = chart.attachEvent('onRowDragStart', function (id: string | number) {
            if (chartTaskIsProjectTimeline(chart, id)) {
                return false;
            }
            if (!chart.isTaskExists(id)) {
                return true;
            }
            const task = chart.getTask(id);
            rowMoveContextRef.current = { movedId: String(id), oldParent: task.parent };
            return true;
        });

        const rowDragEndHandlerId = chart.attachEvent('onRowDragEnd', function (id: string | number) {
            if (!chart.isTaskExists(id)) {
                return true;
            }
            const task = chart.getTask(id);
            syncGanttRowMoveToServer(chart, String(id), task.parent);
            return true;
        });

        const beforeTaskDragHandlerId = chart.attachEvent('onBeforeTaskDrag', function (id: string | number) {
            const denialReason = ganttTaskMoveDenialReasonById(id);
            if (denialReason) {
                // Explain why the drag is blocked before it starts, then cancel it.
                notifyGanttMoveDenied(denialReason);
                return false;
            }
            return true;
        });

        // Event handlers for drag operations - update task dates (debounced: DHTMLX can emit bursts)
        chart.attachEvent('onAfterTaskDrag', function (id: string, mode: string) {
            if (mode !== 'move' && mode !== 'resize') {
                return;
            }
            const afterDragDenialReason = ganttTaskMoveDenialReasonById(id);
            if (afterDragDenialReason) {
                notifyGanttMoveDenied(afterDragDenialReason);
                chart.clearAll();
                const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasksRef.current, projectTimelineLabelRef.current);
                chart.parse({
                    data: ganttTasks,
                    links: ganttLinks,
                });
                restoreScrollState(chart);
                syncTodayMarker();
                return;
            }
            if (!chart.isTaskExists(String(id))) {
                return;
            }
            captureScrollState(chart);
            const draggedTask = chart.getTask(String(id)) as {
                is_project_timeline?: boolean;
                start_date: string | Date;
                end_date?: string | Date;
                duration?: number;
            };
            if (draggedTask?.is_project_timeline) {
                const { startYmd, endYmd } = normalizeDraggedTaskDates(draggedTask);
                setPendingTimelineMove({
                    taskId: String(id),
                    startDate: startYmd,
                    endDate: endYmd,
                });
                setTimelineMoveReason('');
                setTimelineReasonModalOpen(true);
                return;
            }
            if (timelineDragSaveTimerRef.current !== null) {
                window.clearTimeout(timelineDragSaveTimerRef.current);
            }
            const taskId = String(id);
            timelineDragSaveTimerRef.current = window.setTimeout(() => {
                timelineDragSaveTimerRef.current = null;
                if (!chart.isTaskExists(taskId)) {
                    return;
                }
                const task = chart.getTask(taskId);
                const { startYmd, endYmd } = normalizeDraggedTaskDates(
                    task as { start_date: string | Date; end_date?: string | Date; duration?: number },
                );

                const taskData = {
                    start_date: startYmd,
                    end_date: endYmd,
                };

                void (async () => {
                    try {
                        await laravelJsonFetch(route('tasks.update', taskId), 'PUT', taskData);
                        patchCachedTask(taskId, {
                            start_date: taskData.start_date,
                            end_date: taskData.end_date,
                        });
                        toast.success(t('projects.gantt.messages.task_updated', 'Task updated successfully'));
                    } catch {
                        toast.error(t('projects.gantt.messages.task_update_failed', 'Failed to update task'));
                        chart.clearAll();
                        const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasksRef.current, projectTimelineLabelRef.current);
                        chart.parse({
                            data: ganttTasks,
                            links: ganttLinks,
                        });
                        restoreScrollState(chart);
                        syncTodayMarker();
                    }
                })();
            }, GANTT_SERVER_WRITE_DEBOUNCE_MS);
        });

        // Event handlers for link (relationship) operations
        chart.attachEvent('onBeforeLinkAdd', function (_id: string, link: LinkData) {
            const sourceTask = tasksRef.current.find((task) => task.id === String(link.source));
            const targetTask = tasksRef.current.find((task) => task.id === String(link.target));
            if (sourceTask?.is_project_timeline || targetTask?.is_project_timeline) {
                toast.error(
                    t('projects.gantt.messages.timeline_dependency_forbidden', 'Project timeline cannot have dependencies or relationships.'),
                );
                return false;
            }
            return true;
        });

        chart.attachEvent('onAfterLinkAdd', function (id: string, link: LinkData) {
            const sourceTaskId = String(link.source);
            const targetTaskId = String(link.target);
            const linkId = String(id);
            const sourceTask = tasksRef.current.find((task) => task.id === sourceTaskId);
            const targetTask = tasksRef.current.find((task) => task.id === targetTaskId);

            if (sourceTask?.is_project_timeline || targetTask?.is_project_timeline) {
                toast.error(
                    t('projects.gantt.messages.timeline_dependency_forbidden', 'Project timeline cannot have dependencies or relationships.'),
                );
                chart.deleteLink(linkId);
                return;
            }

            const timers = linkRelationshipAddTimersRef.current;
            const previous = timers.get(linkId);
            if (previous !== undefined) {
                window.clearTimeout(previous);
            }

            timers.set(
                linkId,
                window.setTimeout(() => {
                    timers.delete(linkId);

                    const currentTask = tasksRef.current.find((t) => t.id === targetTaskId);
                    if (!currentTask) {
                        return;
                    }

                    const currentDependencies = currentTask.dependencies || [];
                    if (currentDependencies.includes(sourceTaskId)) {
                        return;
                    }
                    const newDependencies = [...currentDependencies, sourceTaskId];

                    void (async () => {
                        try {
                            await laravelJsonFetch(route('tasks.update', targetTaskId), 'PUT', {
                                dependencies: newDependencies,
                            });
                            patchCachedTask(targetTaskId, { dependencies: newDependencies });
                            toast.success(t('projects.gantt.messages.relationship_added', 'Relationship added successfully'));
                        } catch {
                            toast.error(t('projects.gantt.messages.relationship_add_failed', 'Failed to add relationship'));
                            chart.deleteLink(linkId);
                        }
                    })();
                }, GANTT_SERVER_WRITE_DEBOUNCE_MS),
            );
        });

        // Confirm before deleting links
        chart.attachEvent('onBeforeLinkDelete', function () {
            return confirm(t('projects.gantt.messages.confirm_delete_link', 'Are you sure you want to remove this task dependency?'));
        });

        chart.attachEvent('onAfterLinkDelete', function (id: string, link: LinkData) {
            const sourceTaskId = String(link.source);
            const targetTaskId = String(link.target);
            const linkId = String(id);
            const linkType = link.type || '0';

            const timers = linkRelationshipDeleteTimersRef.current;
            const previous = timers.get(linkId);
            if (previous !== undefined) {
                window.clearTimeout(previous);
            }

            timers.set(
                linkId,
                window.setTimeout(() => {
                    timers.delete(linkId);

                    const currentTask = tasksRef.current.find((t) => t.id === targetTaskId);
                    if (!currentTask) {
                        return;
                    }

                    const currentDependencies = currentTask.dependencies || [];
                    const newDependencies = currentDependencies.filter((depId) => depId !== sourceTaskId);

                    void (async () => {
                        try {
                            await laravelJsonFetch(route('tasks.update', targetTaskId), 'PUT', {
                                dependencies: newDependencies,
                            });
                            patchCachedTask(targetTaskId, { dependencies: newDependencies });
                            toast.success(t('projects.gantt.messages.relationship_removed', 'Relationship removed successfully'));
                        } catch {
                            toast.error(t('projects.gantt.messages.relationship_remove_failed', 'Failed to remove relationship'));
                            chart.addLink({
                                id: linkId,
                                source: sourceTaskId,
                                target: targetTaskId,
                                type: linkType,
                            });
                        }
                    })();
                }, GANTT_SERVER_WRITE_DEBOUNCE_MS),
            );
        });

        let resizeObserver: ResizeObserver | null = null;
        let bootstrapFrameId: number | null = null;
        let resyncFrameId: number | null = null;
        let chartInitialized = false;

        const resyncChartSize = () => {
            const container = ganttContainer.current;
            if (!container || container.clientWidth <= 0 || container.clientHeight <= 0 || !chartInitialized) {
                return;
            }
            chart.setSizes();
            chart.render();
            syncTodayMarker();
        };

        const initializeChart = () => {
            try {
                chart.init(mountEl);
                chart.clearAll();

                const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasks, projectTimelineLabel);
                chart.parse({
                    data: ganttTasks,
                    links: ganttLinks,
                });

                chart.eachTask(function (task: { id: string }) {
                    if (chart.hasChild(task.id)) {
                        chart.open(task.id);
                    }
                });

                chart.showDate(getGanttScrollAnchor(currentViewMode, getProjectStartDate));
                restoreScrollState(chart);
                syncTodayMarker();
                chartInitialized = true;

                resyncFrameId = window.requestAnimationFrame(resyncChartSize);
                resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resyncChartSize);
                if (resizeObserver) {
                    resizeObserver.observe(mountEl);
                }
            } catch {
                //
            }
        };

        const bootstrapWhenVisible = () => {
            const width = mountEl.clientWidth;
            const height = mountEl.clientHeight;
            if (width <= 0 || height <= 0) {
                bootstrapFrameId = window.requestAnimationFrame(bootstrapWhenVisible);
                return;
            }
            initializeChart();
        };

        bootstrapFrameId = window.requestAnimationFrame(bootstrapWhenVisible);

        return () => {
            if (timelineDragSaveTimerRef.current !== null) {
                window.clearTimeout(timelineDragSaveTimerRef.current);
                timelineDragSaveTimerRef.current = null;
            }
            linkAddTimersForCleanup.forEach((timerId) => {
                window.clearTimeout(timerId);
            });
            linkAddTimersForCleanup.clear();
            linkDeleteTimersForCleanup.forEach((timerId) => {
                window.clearTimeout(timerId);
            });
            linkDeleteTimersForCleanup.clear();
            if (bootstrapFrameId !== null) {
                window.cancelAnimationFrame(bootstrapFrameId);
            }
            if (resyncFrameId !== null) {
                window.cancelAnimationFrame(resyncFrameId);
            }
            resizeObserver?.disconnect();
            try {
                chart.detachEvent(contextMenuHandlerId);
            } catch {
                //
            }
            try {
                chart.detachEvent(taskClickHandlerId);
            } catch {
                //
            }
            try {
                chart.detachEvent(rowDragStartHandlerId);
            } catch {
                //
            }
            try {
                chart.detachEvent(rowDragEndHandlerId);
            } catch {
                //
            }
            try {
                chart.detachEvent(beforeTaskDragHandlerId);
            } catch {
                //
            }
            try {
                chart.clearAll();
            } catch {
                //
            }
            // Keep singleton lifecycle simple for stable remounts in tabbed layouts.
            chartRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- currentViewMode changes are applied in setViewMode; listing it would re-init the whole chart on every view toggle
    }, [
        tasks,
        appLocale,
        t,
        getProjectStartDate,
        syncTodayMarker,
        syncGanttRowMoveToServer,
        isFullscreen,
        buildTaskBarTextHtml,
        buildGanttGridColumnsConfig,
        ganttInstanceFactory,
        ganttSingleton,
        canUpdateGanttTaskById,
        projectTimelineLabel,
    ]);

    const statusMenuTask = statusContextMenu ? tasks.find((row) => row.id === statusContextMenu.taskId) : null;

    const pickGanttTaskStatus = (statusValue: string) => {
        if (!statusContextMenu) {
            return;
        }
        const { taskId } = statusContextMenu;
        const current = tasksRef.current.find((row) => row.id === taskId);
        if (current?.status === statusValue) {
            setStatusContextMenu(null);
            setStatusMenuPosition(null);
            return;
        }
        setStatusContextMenu(null);
        setStatusMenuPosition(null);
        void (async () => {
            try {
                await laravelJsonFetch(route('tasks.update', taskId), 'PUT', { status: statusValue });
                patchCachedTask(taskId, { status: statusValue as ProjectTask['status'] });
                patchChartTask(taskId, { status: statusValue as ProjectTask['status'] });
                toast.success(t('projects.gantt.messages.status_updated', 'Task status updated'));
            } catch {
                toast.error(t('projects.gantt.messages.status_update_failed', 'Failed to update task status'));
            }
        })();
    };

    const cancelTimelineMove = useCallback(() => {
        const chart = chartRef.current;
        if (chart) {
            chart.clearAll();
            const { tasks: ganttTasks, links: ganttLinks } = transformTasksToGantt(tasksRef.current, projectTimelineLabelRef.current);
            chart.parse({
                data: ganttTasks,
                links: ganttLinks,
            });
            restoreScrollState(chart);
            syncTodayMarker();
        }
        setPendingTimelineMove(null);
        setTimelineMoveReason('');
        setTimelineReasonModalOpen(false);
    }, [restoreScrollState, syncTodayMarker]);

    const saveTimelineMoveWithReason = useCallback(() => {
        if (!pendingTimelineMove) {
            return;
        }
        const reason = timelineMoveReason.trim();
        if (reason.length < 3) {
            toast.error(
                t('projects.gantt.messages.timeline_reason_required', 'Please provide a short justification before moving the project timeline.'),
            );
            return;
        }

        // Guard against DHTMLX ever handing us an unparseable date: sending an
        // empty start/end would fail server validation with an opaque error.
        if (!pendingTimelineMove.startDate || !pendingTimelineMove.endDate) {
            toast.error(t('projects.gantt.messages.task_update_failed', 'Failed to update task'));
            cancelTimelineMove();
            return;
        }

        void (async () => {
            try {
                await laravelJsonFetch(route('tasks.update', pendingTimelineMove.taskId), 'PUT', {
                    start_date: pendingTimelineMove.startDate,
                    end_date: pendingTimelineMove.endDate,
                    timeline_change_reason: reason,
                });
                patchCachedTask(pendingTimelineMove.taskId, {
                    start_date: pendingTimelineMove.startDate,
                    end_date: pendingTimelineMove.endDate,
                });
                setPendingTimelineMove(null);
                setTimelineMoveReason('');
                setTimelineReasonModalOpen(false);
                toast.success(t('projects.gantt.messages.task_updated', 'Task updated successfully'));
            } catch (error) {
                // Surface the server's actual reason (permission, validation, …)
                // instead of a generic message, so failures are diagnosable.
                const message =
                    error instanceof LaravelJsonRequestError && error.message
                        ? error.message
                        : t('projects.gantt.messages.task_update_failed', 'Failed to update task');
                toast.error(message);
                cancelTimelineMove();
            }
        })();
    }, [cancelTimelineMove, patchCachedTask, pendingTimelineMove, t, timelineMoveReason]);

    const renderGanttControls = (instance: 'inline' | 'fullscreen') => (
        <div className="gantt-toolbar-stack flex shrink-0 flex-col gap-3">
            <Card className="gap-0 py-0">
                <CardContent className="flex flex-wrap items-center gap-2 px-3 py-2 sm:justify-between">
                    <div className="flex flex-wrap items-center gap-1">
                        {canCreateTask && (
                            <CreateTaskModal
                                projectId={projectId}
                                users={users}
                                tasks={tasks}
                                isProjectOwner={isProjectOwner}
                                isOpen={createTaskModalOpen}
                                onOpenChange={(nextOpen) => {
                                    setCreateTaskModalOpen(nextOpen);
                                    if (!nextOpen) {
                                        setCreateTaskParentId(null);
                                    }
                                }}
                                initialParentTaskId={createTaskParentId}
                            />
                        )}
                        <Button size="sm" variant="outline" onClick={showTodayRange} className="text-xs">
                            <Calendar className="mr-1 h-3 w-3" />
                            {t('projects.gantt.controls.today', 'Today')}
                        </Button>
                        <span className="text-border mx-1 hidden select-none sm:inline">|</span>
                        <div className="hidden items-center gap-2.5 sm:flex" role="note" aria-label={t('projects.gantt.status.label', 'Status')}>
                            <span className="text-muted-foreground inline-flex items-center gap-1.5 text-[11px]">
                                <span className="bg-success h-2 w-2 shrink-0 rounded-full" aria-hidden />
                                {t('projects.gantt.status.completed', 'Completed')}
                            </span>
                            <span className="text-muted-foreground inline-flex items-center gap-1.5 text-[11px]">
                                <span className="bg-info h-2 w-2 shrink-0 rounded-full" aria-hidden />
                                {t('projects.gantt.status.in_progress', 'In Progress')}
                            </span>
                            <span className="text-muted-foreground inline-flex items-center gap-1.5 text-[11px]">
                                <span className="bg-destructive h-2 w-2 shrink-0 rounded-full" aria-hidden />
                                {t('projects.gantt.status.blocked', 'Blocked')}
                            </span>
                            <span className="text-muted-foreground inline-flex items-center gap-1.5 text-[11px]">
                                <span className="bg-muted-foreground/50 h-2 w-2 shrink-0 rounded-full" aria-hidden />
                                {t('projects.gantt.status.not_started', 'Not Started')}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <TooltipProvider>
                            <div className="flex items-center gap-1">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="h-9 w-9 shrink-0 p-0"
                                            onClick={zoomOut}
                                            disabled={zoomIndex === 0}
                                            aria-label={t('projects.gantt.controls.zoom_out', 'Zoom out')}
                                        >
                                            <ZoomOut className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{t('projects.gantt.controls.zoom_out', 'Zoom out')}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="h-9 w-9 shrink-0 p-0"
                                            onClick={zoomIn}
                                            disabled={zoomIndex === GANTT_ZOOM_LEVELS.length - 1}
                                            aria-label={t('projects.gantt.controls.zoom_in', 'Zoom in')}
                                        >
                                            <ZoomIn className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{t('projects.gantt.controls.zoom_in', 'Zoom in')}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="h-9 w-9 shrink-0 p-0"
                                            aria-pressed={showGridDateColumns}
                                            onClick={toggleGridDateColumnsVisibility}
                                            aria-label={
                                                showGridDateColumns
                                                    ? t('projects.gantt.controls.less_details', 'Less details')
                                                    : t('projects.gantt.controls.more_details', 'More details')
                                            }
                                        >
                                            {showGridDateColumns ? (
                                                <PanelLeftClose className="h-4 w-4" aria-hidden />
                                            ) : (
                                                <CalendarRange className="h-4 w-4" aria-hidden />
                                            )}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        {showGridDateColumns
                                            ? t('projects.gantt.controls.less_details', 'Less details')
                                            : t('projects.gantt.controls.more_details', 'More details')}
                                    </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={departmentSortActive ? 'default' : 'outline'}
                                            className="h-9 w-9 shrink-0 p-0"
                                            onClick={() => onToggleDepartmentSort?.()}
                                            aria-label={t('projects.gantt.sort_by_department', 'Sort by department')}
                                        >
                                            <ArrowUpDown className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{t('projects.gantt.sort_by_department', 'Sort by department')}</TooltipContent>
                                </Tooltip>
                                <div className="relative max-w-[16rem] min-w-[10rem]">
                                    <Search
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 z-10 h-4 w-4 -translate-y-1/2"
                                        aria-hidden
                                    />
                                    <Input
                                        id={instance === 'fullscreen' ? 'gantt-task-search-fullscreen' : 'gantt-task-search-inline'}
                                        type="search"
                                        placeholder={t('projects.gantt.search.placeholder', 'Search tasks...')}
                                        value={searchTerm}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        className="h-9 pl-9 shadow-xs"
                                        aria-label={t('projects.gantt.search.placeholder', 'Search tasks...')}
                                    />
                                </div>
                            </div>
                        </TooltipProvider>
                    </div>
                </CardContent>
            </Card>
        </div>
    );

    const renderGanttChart = (containerRef: React.RefObject<HTMLDivElement | null>) => (
        <div ref={containerRef} className="gantt-chart-area min-h-[14rem] w-full flex-1" />
    );

    const fullscreenOverlay =
        isFullscreen &&
        createPortal(
            <div className={`gantt-fullscreen-overlay ${isFullscreen ? 'active' : ''}`}>
                <div className="gantt-fullscreen-content">
                    {/* Close button */}
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={toggleFullscreen}
                        className="bg-background/95 absolute top-4 right-4 z-50 backdrop-blur-sm"
                    >
                        <X className="h-4 w-4" />
                    </Button>

                    <div className="gantt-container-fullscreen flex min-h-0 flex-1 flex-col gap-3">
                        {renderGanttControls('fullscreen')}
                        <Card className="flex min-h-[18rem] min-w-0 flex-1 flex-col overflow-hidden py-0">
                            <CardContent
                                className="gantt-container bg-card text-card-foreground flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-0 p-0 shadow-none"
                                style={ganttMarkerShiftStyle}
                            >
                                {renderGanttChart(fullscreenGanttContainer)}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>,
            document.body,
        );

    return (
        <>
            <div className="flex h-full min-h-0 flex-col gap-3">
                {renderGanttControls('inline')}
                <Card className="flex min-h-[18rem] min-w-0 flex-1 flex-col overflow-hidden py-0">
                    <CardContent
                        className="gantt-container bg-card text-card-foreground flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-0 p-0 shadow-none"
                        style={ganttMarkerShiftStyle}
                    >
                        <style>{`
          .gantt-container {
            width: 100%;
            height: 100%;
            min-height: 0;
            flex: 1 1 auto;
            --gantt-timeline-weekday-bg: oklch(1 0 0);
            --gantt-timeline-weekend-bg: oklch(0.98 0 0);
          }
          .dark .gantt-container {
            --gantt-timeline-weekday-bg: var(--background);
            --gantt-timeline-weekend-bg: color-mix(in oklch, var(--muted) 22%, var(--background));
          }
          .gantt-container .gantt_container,
          .gantt-container-fullscreen .gantt_container {
            /* The Card around the chart already draws the outer edge; DHTMLX
               draws its own on the layout root, which reads as a double border.
               The portfolio and MPP viewers zero theirs the same way. */
            border: 0 !important;
            --dhx-gantt-grid-body-background: var(--card);
            --dhx-gantt-grid-scale-background: var(--card);
            --dhx-gantt-grid-scale-color: var(--muted-foreground);
            --dhx-gantt-grid-scale-border-vertical: none;
            --dhx-gantt-grid-cell-border: 0 solid transparent;
            --dhx-gantt-scale-border-horizontal: 1px solid color-mix(in oklch, var(--border) 32%, transparent);
            --dhx-gantt-scale-border-vertical: 1px solid color-mix(in oklch, var(--border) 38%, transparent);
            --dhx-gantt-task-row-border: 1px solid color-mix(in oklch, var(--border) 24%, transparent);
            --dhx-gantt-task-row-background: var(--card);
            --dhx-gantt-task-row-background--odd: var(--card);
            --dhx-gantt-container-background: var(--card);
            --dhx-gantt-base-colors-hover-color: color-mix(in oklch, var(--muted) 6%, var(--card));
            --dhx-gantt-base-colors-select: color-mix(in oklch, var(--accent) 14%, var(--card));
          }
          .gantt-container .gantt_container .gantt_task_bg,
          .gantt-container-fullscreen .gantt_container .gantt_task_bg {
            background: var(--gantt-timeline-weekday-bg) !important;
            position: relative !important;
            z-index: 0 !important;
          }
          .gantt-container .gantt_container .gantt_task_area,
          .gantt-container-fullscreen .gantt_container .gantt_task_area {
            background: var(--gantt-timeline-weekday-bg) !important;
          }
          .gantt-container .gantt_container .gantt_data_area,
          .gantt-container-fullscreen .gantt_container .gantt_data_area {
            position: relative !important;
            z-index: 0 !important;
          }
          .gantt-container .gantt_container .gantt_task_cell,
          .gantt-container-fullscreen .gantt_container .gantt_task_cell {
            border: none !important;
            border-right: none !important;
            border-left: none !important;
            border-top: none !important;
            border-bottom: none !important;
            background: var(--gantt-timeline-weekday-bg) !important;
            z-index: 0 !important;
          }
          .gantt-container .gantt_container .gantt_task_cell.weekend,
          .gantt-container-fullscreen .gantt_container .gantt_task_cell.weekend {
            background: var(--gantt-timeline-weekend-bg) !important;
          }
          .gantt-container .gantt_container .gantt_task_scale,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale {
            position: relative !important;
            z-index: 2 !important;
            border-bottom: none !important;
            background: var(--gantt-timeline-weekday-bg) !important;
            overflow: visible !important;
          }
          .gantt-container .gantt_container .gantt_task_scale::after,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale::after {
            content: '' !important;
            position: absolute !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            height: 1px !important;
            z-index: 0 !important;
            pointer-events: none !important;
            background: color-mix(in oklch, var(--border) 55%, transparent) !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_cell,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_cell {
            z-index: 1 !important;
            border: none !important;
            border-right: none !important;
            border-left: none !important;
            border-top: none !important;
            border-bottom: none !important;
            background: var(--gantt-timeline-weekday-bg) !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_cell.weekend,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_cell.weekend {
            background: var(--gantt-timeline-weekend-bg) !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:first-child:not(:last-child),
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:first-child:not(:last-child) {
            border-bottom: 1px solid color-mix(in oklch, var(--border) 45%, transparent) !important;
            box-sizing: border-box !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:first-child:not(:last-child) .gantt_scale_cell,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:first-child:not(:last-child) .gantt_scale_cell {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            min-height: 28px !important;
            box-sizing: border-box !important;
            overflow: visible !important;
            white-space: normal !important;
            word-break: break-word !important;
            line-height: 1.3 !important;
            font-size: 12px !important;
            font-weight: 600 !important;
            color: var(--foreground) !important;
            text-align: left !important;
            padding: 4px 8px 4px 10px !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:not(:first-child) .gantt_scale_cell,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:not(:first-child) .gantt_scale_cell,
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 22px !important;
            box-sizing: border-box !important;
            overflow: visible !important;
            font-size: 12px !important;
            font-weight: 500 !important;
            color: var(--muted-foreground) !important;
            text-align: center !important;
            padding: 3px 4px 3px 4px !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell {
            min-height: 26px !important;
            padding: 5px 6px 5px 6px !important;
            box-sizing: border-box !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:not(:first-child) .gantt_scale_cell.gantt_today:not(.weekend),
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell.gantt_today:not(.weekend),
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:not(:first-child) .gantt_scale_cell.gantt_today:not(.weekend),
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell.gantt_today:not(.weekend) {
            color: #ffffff !important;
            font-weight: 600 !important;
            z-index: 80 !important;
            outline: none !important;
            box-shadow: none !important;
            background: radial-gradient(circle 11px at 50% 50%, #dc2626 0 99%, transparent 100%),
              linear-gradient(var(--gantt-timeline-weekday-bg), var(--gantt-timeline-weekday-bg)) !important;
          }
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:not(:first-child) .gantt_scale_cell.gantt_today.weekend,
          .gantt-container .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell.gantt_today.weekend,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:not(:first-child) .gantt_scale_cell.gantt_today.weekend,
          .gantt-container-fullscreen .gantt_container .gantt_task_scale .gantt_scale_line:first-child:last-child .gantt_scale_cell.gantt_today.weekend {
            color: #ffffff !important;
            font-weight: 600 !important;
            z-index: 80 !important;
            outline: none !important;
            box-shadow: none !important;
            background: radial-gradient(circle 11px at 50% 50%, #dc2626 0 99%, transparent 100%),
              linear-gradient(var(--gantt-timeline-weekend-bg), var(--gantt-timeline-weekend-bg)) !important;
          }
          .gantt-container .gantt_container .gantt_task_area,
          .gantt-container-fullscreen .gantt_container .gantt_task_area {
            position: relative !important;
            z-index: 0 !important;
          }
          .gantt-container .gantt_container .gantt_marker_area,
          .gantt-container-fullscreen .gantt_container .gantt_marker_area {
            position: absolute !important;
            inset: 0 !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            overflow: visible !important;
            z-index: 40 !important;
            pointer-events: none !important;
          }
          .gantt-container .gantt_marker_area .gantt_marker.gantt_marker_today_line,
          .gantt-container-fullscreen .gantt_marker_area .gantt_marker.gantt_marker_today_line {
            width: 2px !important;
            min-width: 2px !important;
            background: #dc2626 !important;
            opacity: 1 !important;
            z-index: 41 !important;
            pointer-events: none !important;
            transform: translateX(var(--gantt-today-marker-shift, 35px)) !important;
          }
          .gantt-container .gantt_container .gantt_task_row,
          .gantt-container-fullscreen .gantt_container .gantt_task_row {
            border-bottom: none !important;
          }
          .gantt-container .gantt_container .gantt_ver_line,
          .gantt-container-fullscreen .gantt_container .gantt_ver_line {
            border-left: none !important;
            background: transparent !important;
            opacity: 0 !important;
          }
          .gantt-chart-area {
            flex: 1 1 auto;
            min-height: ${GANTT_INLINE_MIN_HEIGHT}px;
            min-width: 0;
            width: 100%;
            height: 100%;
          }
          .gantt-container-fullscreen {
            display: flex;
            flex-direction: column;
            height: 100vh;
            width: 100%;
            min-height: 0;
          }
          .gantt-fullscreen-overlay {
            position: fixed;
            inset: 0;
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 0, 0, 0.85);
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s ease, visibility 0.2s ease;
          }
          .gantt-fullscreen-overlay.active {
            opacity: 1;
            visibility: visible;
          }
          .gantt-fullscreen-content {
            display: flex;
            flex-direction: column;
            width: 95vw;
            height: 95vh;
            min-height: 0;
            position: relative;
          }
          .gantt-container .gantt_container .gantt_grid,
          .gantt-container-fullscreen .gantt_container .gantt_grid {
            border: none !important;
            max-height: none !important;
            background: var(--card) !important;
            box-shadow: none !important;
          }
          .gantt-container .gantt_container .gantt_grid_data,
          .gantt-container-fullscreen .gantt_container .gantt_grid_data {
            background: var(--card) !important;
          }
          .gantt-container .gantt_grid_data .gantt_row,
          .gantt-container-fullscreen .gantt_grid_data .gantt_row {
            height: ${GANTT_ROW_HEIGHT}px !important;
          }
          .gantt-container .gantt_container .gantt_grid_scale,
          .gantt-container-fullscreen .gantt_container .gantt_grid_scale {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            box-sizing: border-box !important;
            background-image: none !important;
            box-shadow: none !important;
          }
          .gantt-container .gantt_container .gantt_grid_scale:after,
          .gantt-container-fullscreen .gantt_container .gantt_grid_scale:after {
            display: none !important;
          }
          .gantt-container .gantt_container .gantt_grid_head_cell,
          .gantt-container-fullscreen .gantt_container .gantt_grid_head_cell {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            text-align: left !important;
            min-height: 32px !important;
            padding: 5px 10px !important;
            box-sizing: border-box !important;
            font-size: 12px !important;
            font-weight: 500 !important;
            color: var(--muted-foreground) !important;
            border-right: none !important;
          }
          .gantt-container .gantt-grid-head-label,
          .gantt-container-fullscreen .gantt-grid-head-label {
            display: inline-flex !important;
            align-items: center !important;
            gap: 8px !important;
            min-width: 0 !important;
            width: 100% !important;
            color: inherit !important;
          }
          .gantt-container .gantt-grid-head-icon,
          .gantt-container-fullscreen .gantt-grid-head-icon {
            flex: 0 0 auto !important;
            color: var(--muted-foreground) !important;
            opacity: 0.72 !important;
          }
          .gantt-container .gantt-grid-head-text,
          .gantt-container-fullscreen .gantt-grid-head-text {
            min-width: 0 !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }
          .gantt-container .gantt-grid-head-label--compact,
          .gantt-container-fullscreen .gantt-grid-head-label--compact {
            justify-content: center !important;
            gap: 0 !important;
            width: 100% !important;
          }
          .gantt-container .gantt-grid-head-label--compact .gantt-grid-head-text,
          .gantt-container-fullscreen .gantt-grid-head-label--compact .gantt-grid-head-text {
            font-size: 11px !important;
            font-weight: 600 !important;
            letter-spacing: -0.02em !important;
          }
          .gantt-container .gantt_container .gantt_grid_head_cell:nth-child(2),
          .gantt-container .gantt_container .gantt_grid_head_cell:nth-child(3),
          .gantt-container-fullscreen .gantt_container .gantt_grid_head_cell:nth-child(2),
          .gantt-container-fullscreen .gantt_container .gantt_grid_head_cell:nth-child(3) {
            justify-content: center !important;
            text-align: center !important;
            padding-left: 4px !important;
            padding-right: 4px !important;
          }
          .gantt-container .gantt-grid-date-cell,
          .gantt-container-fullscreen .gantt-grid-date-cell {
            display: inline-block;
            width: 100%;
            text-align: center;
            font-size: 11px !important;
            line-height: 1.15 !important;
            font-variant-numeric: tabular-nums;
            letter-spacing: -0.02em;
            color: var(--muted-foreground);
          }
          .gantt-container .gantt-grid-date-cell--muted,
          .gantt-container-fullscreen .gantt-grid-date-cell--muted {
            opacity: 0.38;
          }
          .gantt-container .gantt_container .gantt_grid .gantt_cell,
          .gantt-container-fullscreen .gantt_container .gantt_grid .gantt_cell {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          .gantt-container .gantt_container .gantt_grid .gantt_cell:nth-child(2),
          .gantt-container .gantt_container .gantt_grid .gantt_cell:nth-child(3),
          .gantt-container-fullscreen .gantt_container .gantt_grid .gantt_cell:nth-child(2),
          .gantt-container-fullscreen .gantt_container .gantt_grid .gantt_cell:nth-child(3) {
            padding-left: 4px !important;
            padding-right: 4px !important;
          }
          .gantt-container .gantt_container .gantt_cell_tree,
          .gantt-container-fullscreen .gantt_container .gantt_cell_tree {
            gap: 10px !important;
          }
          .gantt-container .gantt_cell_tree .gantt_tree_content,
          .gantt-container-fullscreen .gantt_cell_tree .gantt_tree_content {
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: flex-start !important;
            width: 100%;
            min-width: 0;
            min-height: 0;
            white-space: normal !important;
            overflow: hidden;
            text-align: left;
          }
          .gantt-container .gantt-grid-task-cell,
          .gantt-container-fullscreen .gantt-grid-task-cell {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            position: relative;
            min-width: 0;
            width: 100%;
            line-height: 1.25;
          }
          .gantt-container .gantt-grid-task-cell--child::before,
          .gantt-container-fullscreen .gantt-grid-task-cell--child::before {
            content: '';
            position: absolute;
            left: 10px;
            top: -8px;
            bottom: -8px;
            border-left: 1px dashed color-mix(in oklch, var(--muted-foreground) 55%, transparent);
            pointer-events: none;
          }
          .gantt-container .gantt-grid-task-cell--child::after,
          .gantt-container-fullscreen .gantt-grid-task-cell--child::after {
            content: '';
            position: absolute;
            left: 10px;
            top: 50%;
            width: 12px;
            border-top: 1px dashed color-mix(in oklch, var(--muted-foreground) 55%, transparent);
            transform: translateY(-1px);
            pointer-events: none;
          }
          .gantt-container .gantt-grid-status-bubble,
          .gantt-container-fullscreen .gantt-grid-status-bubble {
            flex: 0 0 auto;
            width: 8px;
            height: 8px;
            border-radius: 9999px;
            box-shadow: none !important;
            border: 1px solid color-mix(in oklch, var(--border) 28%, transparent);
          }
          .gantt-container .gantt-grid-status-bubble--child,
          .gantt-container-fullscreen .gantt-grid-status-bubble--child {
            margin-left: 12px;
          }
          .gantt-container .gantt-grid-status-icon,
          .gantt-container-fullscreen .gantt-grid-status-icon {
            flex: 0 0 auto;
            width: 16px;
            height: 16px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .gantt-container .gantt-grid-status-icon svg,
          .gantt-container-fullscreen .gantt-grid-status-icon svg {
            width: 16px;
            height: 16px;
            display: block;
          }
          .gantt-container .gantt-grid-task-text,
          .gantt-container-fullscreen .gantt-grid-task-text {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            gap: 3px;
            flex: 1 1 auto;
            min-width: 0;
            overflow: hidden;
          }
          .gantt-container .gantt-grid-task-cell--project .gantt-grid-task-name,
          .gantt-container-fullscreen .gantt-grid-task-cell--project .gantt-grid-task-name {
            font-size: 12px !important;
            font-weight: 500 !important;
            color: var(--muted-foreground) !important;
          }
          .gantt-container .gantt-grid-task-cell--timeline .gantt-grid-task-name,
          .gantt-container-fullscreen .gantt-grid-task-cell--timeline .gantt-grid-task-name {
            font-weight: 700 !important;
            color: var(--foreground) !important;
          }
          .gantt-container .gantt-grid-task-cell:not(.gantt-grid-task-cell--project) .gantt-grid-task-name,
          .gantt-container-fullscreen .gantt-grid-task-cell:not(.gantt-grid-task-cell--project) .gantt-grid-task-name {
            font-size: 14px !important;
            font-weight: 500 !important;
            color: var(--foreground) !important;
          }
          .gantt-container .gantt-grid-task-name,
          .gantt-container-fullscreen .gantt-grid-task-name {
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .gantt-container .gantt-grid-task-name-wrap,
          .gantt-container-fullscreen .gantt-grid-task-name-wrap {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            white-space: nowrap;
            justify-content: flex-start;
          }
          .gantt-container .gantt-grid-task-name-wrap .gantt-grid-task-name,
          .gantt-container-fullscreen .gantt-grid-task-name-wrap .gantt-grid-task-name {
            flex: 1 1 12ch;
            min-width: 12ch;
            width: auto;
            max-width: none;
          }
          .gantt-container .gantt-grid-add-task-btn,
          .gantt-container-fullscreen .gantt-grid-add-task-btn {
            flex: 0 0 auto;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            border: 1px solid color-mix(in oklch, var(--foreground) 45%, transparent);
            border-radius: 5px;
            background: color-mix(in oklch, var(--muted) 35%, var(--background));
            color: color-mix(in oklch, var(--foreground) 96%, transparent);
            font-size: 14px;
            font-weight: 800;
            line-height: 1;
            cursor: pointer;
            transition: all 0.15s ease;
          }
          .gantt-container .gantt-grid-add-task-btn:hover,
          .gantt-container-fullscreen .gantt-grid-add-task-btn:hover {
            background: color-mix(in oklch, var(--accent) 70%, var(--background));
            color: var(--foreground);
            border-color: color-mix(in oklch, var(--foreground) 62%, transparent);
            transform: translateY(-0.5px);
          }
          .gantt-container .gantt-grid-task-meta,
          .gantt-container-fullscreen .gantt-grid-task-meta {
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 11px !important;
            font-weight: 400 !important;
            color: var(--muted-foreground) !important;
            opacity: 0.92 !important;
          }
          .gantt-container .gantt-grid-task-department-text,
          .gantt-container-fullscreen .gantt-grid-task-department-text {
            display: inline;
          }
          .gantt-container .gantt_task_line,
          .gantt-container-fullscreen .gantt_task_line {
            z-index: 2 !important;
            --gantt-task-status: 215 20% 65%;
            border-radius: 3px !important;
            border: 1px solid hsl(var(--gantt-task-status) / 0.55) !important;
            background: hsl(var(--gantt-task-status) / 0.22) !important;
            box-shadow: none !important;
          }
          .gantt-container .gantt_task_line.gantt_selected,
          .gantt-container-fullscreen .gantt_task_line.gantt_selected {
            border-color: hsl(var(--gantt-task-status) / 0.8) !important;
            background: hsl(var(--gantt-task-status) / 0.32) !important;
            box-shadow: 0 0 0 2px hsl(var(--gantt-task-status) / 0.25) !important;
          }
          .gantt-container .gantt_task_line.gantt_project_timeline,
          .gantt-container-fullscreen .gantt_task_line.gantt_project_timeline {
            height: 8px !important;
            margin-top: 19px !important;
            border-radius: 2px !important;
            border-style: solid !important;
            box-shadow: none !important;
          }
          .gantt-container .gantt_task_line.gantt_project_timeline::before,
          .gantt-container-fullscreen .gantt_task_line.gantt_project_timeline::before {
            display: none !important;
          }
          .gantt-container .gantt_task_line.gantt_project_timeline_ok,
          .gantt-container-fullscreen .gantt_task_line.gantt_project_timeline_ok {
            border-color: #16a34a !important;
            background: #16a34a !important;
          }
          .gantt-container .gantt_task_line.gantt_project_timeline_warning,
          .gantt-container-fullscreen .gantt_task_line.gantt_project_timeline_warning {
            border-color: #eab308 !important;
            background: #eab308 !important;
          }
          .gantt-container .gantt_task_line.gantt_project_timeline_late,
          .gantt-container-fullscreen .gantt_task_line.gantt_project_timeline_late {
            border-color: #dc2626 !important;
            background: #dc2626 !important;
          }
          .gantt-container .gantt_task_line.gantt_project_timeline .gantt_task_content,
          .gantt-container-fullscreen .gantt_task_line.gantt_project_timeline .gantt_task_content {
            display: none !important;
          }
          .gantt-container .gantt_task_line::before,
          .gantt-container-fullscreen .gantt_task_line::before {
            display: none !important;
          }
          .gantt-container .gantt_task_content,
          .gantt-container-fullscreen .gantt_task_content {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
            justify-content: flex-start !important;
            padding-left: 6px !important;
            padding-right: 6px !important;
            text-align: left !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            font-weight: 500 !important;
            color: var(--foreground) !important;
            line-height: 1.2 !important;
            border: none !important;
            border-width: 0 !important;
            border-style: none !important;
            border-color: transparent !important;
            border-image: none !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .gantt-container .gantt_task_content .gantt-task-inline,
          .gantt-container-fullscreen .gantt_task_content .gantt-task-inline {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
          }
          .gantt-container .gantt_task_content .gantt-task-title,
          .gantt-container-fullscreen .gantt_task_content .gantt-task-title {
            flex: 0 1 auto;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 12px;
            font-weight: 500;
            color: var(--foreground);
          }
          .gantt-container .gantt_task_content .gantt-task-sep,
          .gantt-container-fullscreen .gantt_task_content .gantt-task-sep {
            flex: 0 0 auto;
            color: var(--muted-foreground);
            opacity: 0.65;
            font-weight: 400;
            font-size: 12px;
          }
          .gantt-container .gantt_task_content .gantt-task-department,
          .gantt-container-fullscreen .gantt_task_content .gantt-task-department {
            flex: 0 1 auto;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 12px;
            font-weight: 700;
            color: var(--foreground);
          }
          .gantt-container .gantt_task_content .gantt-task-assignee,
          .gantt-container-fullscreen .gantt_task_content .gantt-task-assignee {
            flex: 1 1 auto;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 12px;
            font-weight: 400;
            color: var(--muted-foreground);
          }
          ${ganttStatusThemeRules}
        `}</style>

                        {renderGanttChart(ganttContainer)}
                    </CardContent>
                </Card>
            </div>

            {fullscreenOverlay}

            {typeof document !== 'undefined' &&
                statusContextMenu &&
                createPortal(
                    <div
                        ref={statusMenuPanelRef}
                        role="menu"
                        aria-label={t('projects.gantt.context_menu.title', 'Set status')}
                        className="bg-popover text-popover-foreground fixed z-[200] max-h-[min(20rem,70vh)] min-w-[14rem] overflow-y-auto rounded-md border p-1 shadow-md"
                        style={{
                            left: (
                                statusMenuPosition ?? {
                                    left: statusContextMenu.clientX,
                                    top: statusContextMenu.clientY,
                                }
                            ).left,
                            top: (
                                statusMenuPosition ?? {
                                    left: statusContextMenu.clientX,
                                    top: statusContextMenu.clientY,
                                }
                            ).top,
                        }}
                    >
                        <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                            {t('projects.gantt.context_menu.title', 'Set status')}
                        </div>
                        {projectTaskStatuses.map((s) => {
                            const isCurrent = statusMenuTask?.status === s.value;
                            return (
                                <button
                                    key={s.value}
                                    type="button"
                                    role="menuitem"
                                    className={cn(
                                        'hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                                        isCurrent && 'bg-accent/50',
                                    )}
                                    onClick={() => pickGanttTaskStatus(s.value)}
                                >
                                    <span className={cn('size-2 shrink-0 rounded-full', taskStatusSwatchClass(s.color))} aria-hidden />
                                    <span className="min-w-0 flex-1 truncate">{translateStatusLabel(s.value, s.label)}</span>
                                    {isCurrent ? <Check className="size-4 shrink-0 opacity-70" aria-hidden /> : null}
                                </button>
                            );
                        })}
                    </div>,
                    document.body,
                )}

            <ResponsiveModal
                open={timelineReasonModalOpen}
                onOpenChange={(open) => (open ? setTimelineReasonModalOpen(true) : cancelTimelineMove())}
                variant="sheet"
            >
                <ResponsiveModalContent size="md">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.gantt.timeline_reason.title', 'Project timeline justification')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t('projects.gantt.timeline_reason.description', 'Please explain why project timeline dates were changed.')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="space-y-2">
                        <Input
                            value={timelineMoveReason}
                            onChange={(event) => setTimelineMoveReason(event.target.value)}
                            placeholder={t('projects.gantt.timeline_reason.placeholder', 'Short justification')}
                        />
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={cancelTimelineMove}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={saveTimelineMoveWithReason}>
                            {t('common.save', 'Save')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>

            {/* Edit Task Modal */}
            {selectedTaskForEdit && canOpenTaskModal && (
                <EditTaskModal
                    task={selectedTaskForEdit}
                    users={users}
                    tasks={tasks.filter((t) => t.id !== selectedTaskForEdit.id)}
                    isProjectOwner={isProjectOwner}
                    isOpen={true}
                    onClose={() => setSelectedTaskForEdit(null)}
                    key={selectedTaskForEdit.id} // Force re-render when task changes
                />
            )}
        </>
    );
}
